"use client";

import { createContext, ReactNode, useContext, useEffect } from "react";

import { QueryObserverResult, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { getOrganizationContext } from "@/app/lib/actions/organization";
import { useUser } from "@/app/lib/context/UserContext";
import { OrganizationContextData } from "@/app/lib/definitions/organization";

dayjs.extend(relativeTime);

interface OrganizationContextType {
  organizationData: OrganizationContextData | null | undefined;
  isLoading: boolean;
  refetchOrganization: () => Promise<QueryObserverResult<OrganizationContextData, Error>>;
  getCachedOrganization: () => OrganizationContextData | null | undefined;
  // Convenience getters
  isTrialActive: boolean;
  trialDaysLeft: number | null;
  trialTimeLeft: string | null;
  defaultResponsibilityFramework: number | null;
  defaultAiAgent: string | null;
  hasFeature: (featureName: string) => boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  // Only fetch if user has a current membership
  const tenantId = user?.current_membership?.organization;

  const {
    data: organizationData,
    isLoading,
    refetch,
  } = useQuery({
    enabled: !!tenantId,
    queryKey: ["organization-context", tenantId],
    queryFn: getOrganizationContext,
    staleTime: 5 * 60 * 1000,
  });

  const getCachedOrganization = (): OrganizationContextData | null | undefined => {
    return queryClient.getQueryData(["organization-context", tenantId]);
  };

  const refetchOrganization = () => refetch();

  // Convenience computed values
  const isTrialActive = organizationData?.subscription?.is_on_trial ?? false;

  // Calculate trial days left using dayjs
  const trialDaysLeft = organizationData?.subscription?.trial_end_time
    ? dayjs(organizationData.subscription.trial_end_time).diff(dayjs(), "day")
    : null;

  // Get smart relative time for trial
  const trialTimeLeft = organizationData?.subscription?.trial_end_time
    ? dayjs(organizationData.subscription.trial_end_time).fromNow()
    : null;

  const defaultResponsibilityFramework =
    organizationData?.settings?.responsibility_framework ?? null;

  const defaultAiAgent = organizationData?.settings?.default_ai_agent ?? null;

  const hasFeature = (featureName: string): boolean => {
    return organizationData?.features?.includes(featureName) ?? false;
  };

  useEffect(() => {
    if (organizationData?.organization?.id) {
      localStorage.setItem("currentOrganization", organizationData.organization.id);
    } else {
      localStorage.removeItem("currentOrganization");
    }
  }, [organizationData?.organization?.id]);

  return (
    <OrganizationContext.Provider
      value={{
        organizationData,
        isLoading,
        refetchOrganization,
        getCachedOrganization,
        isTrialActive,
        trialDaysLeft,
        trialTimeLeft,
        defaultResponsibilityFramework,
        defaultAiAgent,
        hasFeature,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};
